import React, { useCallback, useState } from 'react';
import {
  ActionIcon, Box, Divider, Group, type StackProps, ScrollArea, Stack, Text, Tooltip, useMantineTheme
} from '@mantine/core';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { IconLayoutSidebarLeftCollapseFilled, IconEdit, IconChevronDown, IconChevronRight } from '@tabler/icons-react';

import type { SectionData } from '../../Pages/Editor/SectionEditor';
import { getSplitLocation } from '../../utils/stringUtils';
import classes from './EditorSideMenu.module.css';
import { RouterNavLink } from '..';
import { resetSelection } from '../../redux/NarrativeComplianceSlice';
import { getProposalManagementRoute } from '../../utils/routerUtils';

type SideMenuProps = {
  sections: SectionData[]
  hasEditPermission: boolean
  toggleLockedSideMenu: () => void
  tooltipLabel: string
} & StackProps;

interface SectionProps {
  section: SectionData
  isExpanded: boolean
  toggleExpanded: (sectionUid: string) => void
};

const SideMenuSection: React.FC<SectionProps> = (props: SectionProps) => {
  const { section, isExpanded, toggleExpanded } = props;
  const { organization, proposalUid } = useParams();
  const hasChildSections = section.childSections !== undefined && section.childSections.length > 0;
  const { other: colorVars, colors } = useMantineTheme();
  const location = useLocation();

  const dispatch = useDispatch();
  const onClickNavigate = useCallback(() => {
    // reset compliance selection when navigating to prevent erroneous warning toast
    dispatch(resetSelection());
  }, []);

  const sectionChevron = isExpanded
    ? <IconChevronDown
      color={colorVars.primaryIconColor}
      onClick={(event) => { event.stopPropagation(); event.preventDefault(); toggleExpanded(section.uid); }}
    />
    : <IconChevronRight
      color={colorVars.primaryIconColor}
      onClick={(event) => { event.stopPropagation(); event.preventDefault(); toggleExpanded(section.uid); }}
    />;

  return (
    <>
      <RouterNavLink
        leftSection={hasChildSections ? sectionChevron : <Box w={24}></Box>}
        onClick={onClickNavigate}
        active={getSplitLocation(location).includes(section.uid)}
        classNames={{ root: classes.root, section: classes.section }}
        label={
          <Tooltip
            offset={-30}
            label={section.title}
            position='right-start'
          >
            <Text
              fz={14}
              fw={500}
              truncate
            >
              {section.title}
            </Text>
          </Tooltip>
        }
        mb={8}
        to={`/org/${organization}/proposals/${proposalUid}/edit/${section.uid}`}
      />
      {
        isExpanded && <Stack
          gap={8}
          ml={28}
          mb={8}
          style={{
            borderLeft: `4px solid ${colors.lightPurple[2]}`
          }}
        >
          {section.childSections?.map((childSection) => (
            <RouterNavLink
              key={childSection.uid}
              onClick={onClickNavigate}
              active={getSplitLocation(location).includes(childSection.uid)}
              classNames={{ root: classes.subsectionRoot }}
              label={<Tooltip
                offset={-30}
                label={childSection.title}
                position='right-start'
              >
                <Text
                  fz={14}
                  fw={500}
                  truncate
                >
                  {childSection.title}
                </Text>
              </Tooltip>
              }
              to={`/org/${organization}/proposals/${proposalUid}/edit/${childSection.uid}`}
            />
          ))}
        </Stack>
      }
    </>
  );
};

const EditorSideMenu: React.FC<SideMenuProps> = (props: SideMenuProps) => {
  const { toggleLockedSideMenu, tooltipLabel, sections, ...stackProps } = props;
  const { organization, proposalUid } = useParams();

  const [expandedSectionUids, setExpandedSectionUids] = useState<string[]>(
    sections.filter((section) => section.childSections !== undefined && section.childSections.length > 0).map(
      (section) => section.uid
    )
  );
  const navigate = useNavigate();
  const proposalManagementURL = getProposalManagementRoute(organization, proposalUid);
  const { colors, other: colorVars } = useMantineTheme();

  const editButton = (
    <Tooltip
      label='Edit outline'
    >
      <ActionIcon
        variant='subtle'
        onClick={() => { navigate(proposalManagementURL); }}
      >
        <IconEdit color={colorVars.primaryIconColor} />
      </ActionIcon>
    </Tooltip>
  );

  const toggleSectionExpanded = useCallback((sectionUid: string) => {
    if (expandedSectionUids.includes(sectionUid)) {
      setExpandedSectionUids((prev) => prev.filter((uid) => uid !== sectionUid));
    } else {
      setExpandedSectionUids((prev) => [...prev, sectionUid]);
    }
  }, [expandedSectionUids]);
  return (
    <Stack
      gap={0}
      flex={1}
      w='100%'
      bg={colors.lightPurple[0]}
      {...stackProps}
    >
      <Group justify='space-between' mb={8}>
        <Group>
          <Tooltip label={tooltipLabel}>
            <ActionIcon variant='subtle' onClick={toggleLockedSideMenu}>
              <IconLayoutSidebarLeftCollapseFilled color={colorVars.primaryIconColor} />
            </ActionIcon>
          </Tooltip>
          <Text
            ta={'start'}
            fw={500}
          >
            Sections
          </Text>
        </Group>
        <Group gap='xs'>
          {editButton}
        </Group>
      </Group>
      <Divider color={colors.lightPurple[2]} />
      <ScrollArea ml={2} mt={8} mr={2} flex={1}>
        {props.sections.filter((section) => section.parentSectionUid === undefined).map((section) => {
          return (
            <div key={section.uid}>
              <SideMenuSection
                section={section}
                isExpanded={expandedSectionUids.includes(section.uid)}
                toggleExpanded={toggleSectionExpanded}
              />
            </div>
          );
        })}
      </ScrollArea>
    </Stack>
  );
};

export default EditorSideMenu;
